// components/LinkInput.tsx
import React, { useState, useCallback } from 'react';
import { getCareersPageContent } from '@/lib/ui-content/careers-ui-content';
import { LocaleDetails } from 'shared/src/types/languages.type';

interface LinkInputProps {
  id: string;
  label: string;
  prefix: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  locale: LocaleDetails;
}

export const LinkInput: React.FC<LinkInputProps> = ({ id, label, prefix, onChange, locale }) => {
  const [inputValue, setInputValue] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [showSmartSuggestion, setShowSmartSuggestion] = useState(false);

  const content = getCareersPageContent(locale);

  // Extract username/handle from full URL
  const extractUsernameFromUrl = useCallback(
    (url: string): string => {
      try {
        // Remove protocol if present
        let cleanUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');

        // Handle different social media patterns
        if (prefix.includes('linkedin')) {
          const match = cleanUrl.match(/linkedin\.com\/in\/([^\/\?]+)/);
          return match ? match[1] : '';
        } else if (prefix.includes('github')) {
          const match = cleanUrl.match(/github\.com\/([^\/\?]+)/);
          return match ? match[1] : '';
        } else if (prefix.includes('twitter') || prefix.includes('x.com')) {
          const match = cleanUrl.match(/(?:twitter\.com|x\.com)\/([^\/\?]+)/);
          return match ? match[1] : '';
        } else if (prefix.includes('instagram')) {
          const match = cleanUrl.match(/instagram\.com\/([^\/\?]+)/);
          return match ? match[1] : '';
        }

        // Generic pattern: domain.com/username
        const parts = cleanUrl.split('/');
        return parts.length > 1 ? parts[1] : '';
      } catch {
        return '';
      }
    },
    [prefix],
  );

  // Validate if input looks like a URL
  const isUrl = useCallback((value: string): boolean => {
    return (
      value.includes('://') || value.includes('.com') || value.includes('.co') || value.includes('.org') || value.includes('.net')
    );
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);

      const urlCheck = isUrl(value);
      setIsValidUrl(urlCheck);

      if (urlCheck) {
        const extracted = extractUsernameFromUrl(value);
        setShowSmartSuggestion(extracted.length > 0);
      } else {
        setShowSmartSuggestion(false);
      }

      onChange(e);
    },
    [isUrl, extractUsernameFromUrl, onChange],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');

      if (isUrl(pastedText)) {
        e.preventDefault();
        const extracted = extractUsernameFromUrl(pastedText);

        if (extracted) {
          // Create synthetic event for the extracted username
          const target = e.target as HTMLInputElement;
          target.value = extracted;
          setInputValue(extracted);
          setIsValidUrl(false);
          setShowSmartSuggestion(false);

          // Trigger onChange with extracted value
          const syntheticEvent = {
            target: { ...target, value: extracted, name: target.name },
            currentTarget: target,
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    },
    [isUrl, extractUsernameFromUrl, onChange],
  );

  const applySuggestion = useCallback(() => {
    if (isValidUrl) {
      const extracted = extractUsernameFromUrl(inputValue);
      if (extracted) {
        const target = document.getElementById(id) as HTMLInputElement;
        if (target) {
          target.value = extracted;
          setInputValue(extracted);
          setIsValidUrl(false);
          setShowSmartSuggestion(false);

          const syntheticEvent = {
            target: { ...target, value: extracted, name: target.name },
            currentTarget: target,
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    }
  }, [isValidUrl, inputValue, extractUsernameFromUrl, id, onChange]);

  return (
    <div className="group relative">
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-medium text-ink"
      >
        {label}
      </label>

      <div className="relative">
        <div className="flex items-center border-b border-hairline transition-colors duration-200 group-focus-within:border-accent">
          <span className="shrink-0 py-2.5 pr-1 text-base font-light text-muted">
            {prefix}
          </span>
          <input
            type="text"
            id={id}
            name={id}
            className="w-full min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base font-light text-ink placeholder-muted/50 focus:outline-none focus:ring-0"
            placeholder={content.linkInput.placeholderDefault}
            onChange={handleInputChange}
            onPaste={handlePaste}
          />

          {/* URL Detection Indicator */}
          {isValidUrl && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
            </div>
          )}
        </div>

        {/* Smart Suggestion Tooltip */}
        {showSmartSuggestion && (
          <div className="absolute left-0 right-0 top-full z-10 mt-2 rounded-nested border border-hairline bg-card p-3 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="h-4 w-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-light text-muted">
                  {content.linkInput.smartExtract}{' '}
                  <span className="font-medium text-ink">{extractUsernameFromUrl(inputValue)}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={applySuggestion}
                className="rounded-full bg-ink px-3 py-1 text-xs font-medium text-card transition-colors duration-200 hover:bg-accent-strong"
              >
                {content.linkInput.apply}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
